import type { RefObject } from 'react';
import type { ChatTurn } from '../../lib/coachChatSession';

function renderChatContent(text: string) {
  const parts: Array<string | { href: string; label: string }> = [];
  const re = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push({ href: m[2]!, label: m[1]! });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  if (parts.length === 1 && typeof parts[0] === 'string') return <>{text}</>;
  return (
    <>
      {parts.map((p, i) =>
        typeof p === 'string' ? (
          <span key={i}>{p}</span>
        ) : (
          <a
            key={i}
            href={p.href}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="font-medium text-violet-200 underline underline-offset-2"
          >
            {p.label}
          </a>
        ),
      )}
    </>
  );
}

export function CoachChatThread({
  turns,
  busy,
  bottomRef,
}: {
  turns: ChatTurn[];
  busy: boolean;
  bottomRef: RefObject<HTMLDivElement | null>;
}) {
  if (!turns.length && !busy) return null;

  return (
    <div id="coach-chat" className="flex flex-col gap-3 px-1" aria-label="Coach conversation">
      {turns.map((t, i) => {
        const isUser = t.role === 'user';
        return (
          <div key={`${i}-${t.role}`} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[82%] px-3.5 py-2.5 text-[15px] leading-relaxed ${
                isUser
                  ? 'rounded-[22px] rounded-br-md bg-violet-500 text-white shadow-[0_6px_18px_rgba(139,92,246,0.28)]'
                  : 'rounded-[22px] rounded-bl-md bg-white/[0.08] text-[var(--text-primary)]'
              }`}
            >
              <div className="whitespace-pre-wrap break-words">
                {isUser ? t.content : renderChatContent(t.content)}
              </div>
            </div>
          </div>
        );
      })}
      {busy ? (
        <div className="flex justify-start">
          <div className="rounded-[22px] rounded-bl-md bg-white/[0.08] px-4 py-3 text-sm text-[var(--text-secondary)]">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-violet-400" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-violet-400 [animation-delay:120ms]" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-violet-400 [animation-delay:240ms]" />
            </span>
          </div>
        </div>
      ) : null}
      <div ref={bottomRef} className="h-1" />
    </div>
  );
}
